import pick from 'lodash/pick';
import size from 'lodash/size';

import PetConcern from '@/enums/PetConcern';
import QuestionnaireAllergyStage from '@/enums/QuestionnaireAllergyStage';
import QuestionnaireAllergyStep from '@/enums/QuestionnaireAllergyStep';
import QuestionnaireFleaAndTickStage from '@/enums/QuestionnaireFleaAndTickStage';
import QuestionnaireFleaAndTickStep from '@/enums/QuestionnaireFleaAndTickStep';
import QuestionnaireStatus from '@/enums/QuestionnaireStatus';
import QuestionnaireType from '@/enums/QuestionnaireType';
import ServiceType from '@/enums/ServiceType';

export default {
  namespaced: true,

  state: {
    currentStage: null,
    currentQuestion: null,
    inputs: {
      city: null,
      country: null,
      province: null,
    },
    loading: false,
    participation: null,
    pet: null,
    stages: null,
    stagesEnablingBack: [],
    steps: null,
    type: null,
    questionnaire: null,
    questionnaires: [],
  },

  getters: {
    getActiveStep(state) {
      return state.steps ? state.steps.indexOf(state.currentStage) + 1 : null;
    },
    getCompletedQuestionnaire: (state) => (type) => state.questionnaires.find((q) => q.type === type && q.active_participation?.status === QuestionnaireStatus.Completed),
    getCurrentQuestion(state) {
      return state.currentQuestion;
    },
    getInputs(state) {
      return state.inputs;
    },
    getParticipation(state) {
      return state.participation;
    },
    getPet(state) {
      return state.pet;
    },
    getQuestionnaire(state) {
      return state.questionnaire;
    },
    getQuestionnaires(state) {
      return state.questionnaires;
    },
    getQuestionnaireByType: (state) => (type) => state.questionnaires.find((q) => q.type === type),
    getQuestionnaireHealthConcern(state) {
      switch (state.type) {
        case QuestionnaireType.Allergies:
          return PetConcern.Allergies;
        case QuestionnaireType.FleaAndTick:
          return PetConcern.FleaAndTick;
        default:
          return null;
      }
    },
    getQuestionnaireServiceType(state) {
      switch (state.type) {
        case QuestionnaireType.Allergies:
          return ServiceType.Allergies;
        case QuestionnaireType.FleaAndTick:
          return ServiceType.FleaAndTick;
        default:
          return null;
      }
    },
    getQuestionCount(state) {
      return state.questionnaire?.question_count || 3;
    },
    getStepCount(state) {
      return size(state.steps);
    },
    getType(state) {
      return state.type;
    },
    hasParticipation(state) {
      return size(state.participation);
    },
    isFleaAndTickType(state) {
      return state.type === QuestionnaireType.FleaAndTick;
    },
    isAllergyType(state) {
      return state.type === QuestionnaireType.Allergies;
    },
    isLoading(state) {
      return state.loading;
    },
    isParticipationComplete(state) {
      return state.participation?.status === QuestionnaireStatus.Completed;
    },
    isQuestionStage(state) {
      return /^QUESTION\d+$/.test(state.currentStage);
    },
    showHeaderBackButton(state) {
      return state.stagesEnablingBack.some((enabledStage) => {
        if (enabledStage === QuestionnaireFleaAndTickStage.Question) {
          return state.currentStage.startsWith(enabledStage);
        }

        return state.currentStage === enabledStage;
      });
    },
  },

  mutations: {
    setCurrentStage(state, currentStage) {
      state.currentStage = currentStage;
    },
    setCurrentQuestion(state, currentQuestion) {
      state.currentQuestion = currentQuestion;
    },
    setInputs(state, data) {
      state.inputs = {
        ...state.inputs,
        ...pick(data, Object.keys(state.inputs)),
      };
    },
    setLoading(state, loading) {
      state.loading = loading;
    },
    setParticipation(state, participation) {
      state.participation = participation;
    },
    setPet(state, pet) {
      state.pet = pet;
    },
    setQuestionnaire(state, questionnaire) {
      state.questionnaire = questionnaire;
    },
    setQuestionnaires(state, questionnaires) {
      state.questionnaires = questionnaires;
    },
    setStages(state, stages) {
      state.stages = stages;
    },
    setStagesEnablingBack(state, stagesEnablingBack) {
      state.stagesEnablingBack = stagesEnablingBack;
    },
    setSteps(state, steps) {
      state.steps = steps;
    },
    setType(state, type) {
      state.type = type;
    },
  },

  actions: {
    async answerCurrentQuestion({ commit, getters }, payload) {
      const response = await this.$app.$api.post(`/v1/cx/questionnaires/${getters.getParticipation?.uuid}/questions/current`, payload);

      // Data will be empty when the questionnaire is finished
      commit('setCurrentQuestion', response.data?.data);

      return response;
    },

    async createParticipation({ commit, getters }, payload) {
      const response = await this.$app.$api.post(`/v1/cx/questionnaires/${getters.getQuestionnaire?.slug}/participate`, {
        pet_uuid: payload.pet_uuid,
      });

      commit('setParticipation', response.data.data);

      // Update the active_participation in the questionnaires list
      // to keep it in sync without refetching the questionnaires and creating new participation
      // since we just created a new participation
      const index = getters.getQuestionnaires.findIndex((q) => q.type === getters.getQuestionnaire.type);

      if (index !== -1) {
        commit('setQuestionnaires', getters.getQuestionnaires.map((q, i) => (
          i === index
            ? { ...q, active_participation: response.data.data }
            : q
        )));
      }
    },

    async fetchCurrentQuestion({ commit, getters, dispatch }) {
      const response = await this.$app.$api.get(`/v1/cx/questionnaires/${getters.getParticipation?.uuid}/questions/current`);

      commit('setCurrentQuestion', response.data.data);
      dispatch('syncCurrentStageWithQuestion');
    },

    async fetchParticipation({ commit }, payload) {
      const response = await this.$app.$api.get(`v1/cx/questionnaires/${payload.participationUuid}`);
      const { data } = response.data;

      commit('setParticipation', data);

      return data;
    },

    async fetchParticipationQuote({ getters, dispatch, rootGetters }) {
      const response = await dispatch('fetchRecommendation');

      const searchResult = response.data?.data;
      const search = response.data?.search;
      const firstTimeslot = searchResult?.timeslots?.[0];

      if (searchResult && search && firstTimeslot) {
        const quotePayload = {
          partnerUuid: searchResult.feature.uuid,
          client_uuid: rootGetters.getUser.uuid,
          end_at: firstTimeslot.end_at,
          pet_uuid: search.pet_uuid,
          search_result_uuid: searchResult.uuid,
          service_type: getters.getQuestionnaireServiceType,
          start_at: firstTimeslot.start_at,
          target_city: search.city,
          target_country: search.country,
          target_province: search.province,
        };

        const quoteResponse = await dispatch('quote/createQuote', quotePayload, { root: true });

        return quoteResponse.data?.data;
      }

      return null;
    },

    async fetchPetActiveParticipation({ commit, dispatch }, payload = {}) {
      const questionnaire = await dispatch('fetchQuestionnaire', payload);

      // TODO: Gracefully handle when questionnaire is empty
      const activeParticipation = questionnaire?.active_participation;

      if (!activeParticipation) {
        commit('setParticipation', null);
      } else {
        // Fetch full participation data since active_participation lacks latest_result and questions
        await dispatch('fetchParticipation', { participationUuid: activeParticipation?.uuid });
      }
    },

    async fetchQuestionnaire({ commit, state }, payload = {}) {
      const response = await this.$app.$api.get('v1/cx/questionnaires', {
        params: {
          filter: {
            type: state.type,
            ...payload.filter,
          },
        },
      });

      const questionnaires = response.data.data;

      // Data will be empty when species is not supported
      if (size(questionnaires)) {
        const defaultQuestionnaire = questionnaires[0];
        const activeParticipationQuestionnaire = questionnaires.find((q) => q.active_participation !== null);
        const selectedQuestionnaire = activeParticipationQuestionnaire ?? defaultQuestionnaire;

        commit('setQuestionnaire', selectedQuestionnaire);
        commit('setQuestionnaires', questionnaires);
      }

      return questionnaires;
    },

    async fetchRecommendation({ getters }) {
      return this.$app.$api.post(`v1/cx/questionnaires/${getters.getParticipation?.uuid}/recommend`, getters.getInputs);
    },

    initialize({ commit }, payload) {
      commit('setCurrentStage', payload.stage);
      commit('setStagesEnablingBack', payload.stagesEnablingBack);
      commit('setType', payload.type);
    },

    nextStage({ commit, state }) {
      const currentIndex = state.stages.indexOf(state.currentStage);

      if (currentIndex < state.stages.length - 1) {
        const nextStage = state.stages[currentIndex + 1];
        commit('setCurrentStage', nextStage);
      }
    },

    async previousQuestion({ commit, getters, dispatch }) {
      const response = await this.$app.$api.post(`/v1/cx/questionnaires/${getters.getParticipation?.uuid}/questions/previous`);

      commit('setCurrentQuestion', response.data.data);
      dispatch('syncCurrentStageWithQuestion');

      return response;
    },

    previousStage({ commit, state }) {
      const currentIndex = state.stages.indexOf(state.currentStage);

      if (currentIndex > 0) {
        const previousStage = state.stages[currentIndex - 1];
        commit('setCurrentStage', previousStage);
      }
    },

    setStages({ commit, getters }) {
      let stage = null;

      switch (getters.getType) {
        case QuestionnaireType.Allergies:
          stage = QuestionnaireAllergyStage;
          break;
        case QuestionnaireType.FleaAndTick:
        default:
          stage = QuestionnaireFleaAndTickStage;
          break;
      }

      const baseStages = stage.getBaseStages();
      const questionStages = Array.from({ length: getters.getQuestionCount }, (_, index) => `${stage.Question}${index + 1}`);
      const stages = [...baseStages];

      stages.splice(stages.length - 1, 0, ...questionStages);
      commit('setStages', stages);
    },

    // Used for the stepper only
    setSteps({ commit, getters }) {
      let step = null;

      switch (getters.getType) {
        case QuestionnaireType.Allergies:
          step = QuestionnaireAllergyStep;
          break;
        case QuestionnaireType.FleaAndTick:
        default:
          step = QuestionnaireFleaAndTickStep;
          break;
      }

      const baseSteps = step.getBaseSteps();
      const questionSteps = Array.from({ length: getters.getQuestionCount }, (_, index) => `${step.Question}${index + 1}`);
      const steps = baseSteps.concat(questionSteps);

      commit('setSteps', steps);
    },

    syncCurrentStageWithQuestion({ commit, state }) {
      if (state.currentQuestion && state.participation?.questions && state.stages) {
        const questionIndex = state.participation.questions.findIndex(
          (q) => q.slug === state.currentQuestion.slug,
        );

        if (questionIndex === -1) {
          return;
        }

        const targetStage = `${QuestionnaireFleaAndTickStage.Question}${questionIndex + 1}`;

        if (state.stages.includes(targetStage) && state.currentStage !== targetStage) {
          commit('setCurrentStage', targetStage);
        }
      }
    },
  },
};
